import React, { useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Card, Box, Flex, Tabs, Tooltip, Loader, Button, Text, ActionIcon, Skeleton} from '@mantine/core';
import Breadcrumbs from "@/components/breadcrumbs";
import constants from "@/utils/constants";
import SCMessageBarContext from "@/utils/contexts/sc-message-bar-context";
import Router, { useRouter } from "next/router";
import * as Enums from "@/utils/enums";
import Storage from "@/utils/storage";
import Helper from "@/utils/helper";
import tabStyles from "@/styles/Tabs.module.css";
import styles from "@/PageComponents/Table/Table/ScTableData.module.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Fetch from "@/utils/Fetch";
import { StocktakeDto, StockTakeFinancialsDto, StocktakeItemDto } from "@/PageComponents/Stock Take/StockTake.model";
import StockTakeDetail from "@/PageComponents/Stock Take/StockTakeDetail";
import StockTakeItemList from '@/PageComponents/Stock Take/StockTakeItemList';
import { showNotification } from "@mantine/notifications";
import * as enums from "@/utils/enums";
import {
    IconArrowBackUp,
    IconCheck,
    IconChecklist, IconCheckupList, IconChevronLeft,
    IconCircleX, IconCopy,
    IconPlayerPlay, IconRefresh
} from "@tabler/icons-react";
import Link from "next/link";
import PS from "@/services/permission/permission-service";
import storageService from "@/utils/storage";
import featureService from "@/services/feature/feature-service";
import ConfirmAction from '@/components/modals/confirm-action';
import StocktakeTimeline from "@/PageComponents/Stock Take/StocktakeTimeline";
import {useDebouncedCallback, useDidUpdate, useMediaQuery } from '@mantine/hooks';
import stockService from '@/services/stock/stock-service';
import time from '@/utils/time';
import CompanyService from "@/services/company-service";
import { createFileRoute } from '@tanstack/react-router'

// Request method for company currency symbol (kept outside of components per guidelines)
const fetchCompanyCurrencySymbol = async () => {
    try {
        const symbol = await CompanyService.getCurrencySymbol();
        return symbol || '';
    } catch (e) {
        return '';
    }
};

const StockTake: React.FC = () => {

    const messageBarContext = useContext(SCMessageBarContext);

    const router = useRouter()

    const buttonIconMode = useMediaQuery('(max-width: 900px)');

    // Fetch the feature flag to check if stock take is enabled.
    // Redirect to home page if the feature is not available.
    const hasStockTakeQuery = useQuery(['hasStockTake'], () => featureService.getFeature(constants.features.STOCK_TAKE))

    // Redirect if stock take feature is not available
    useEffect(() => {
        if (hasStockTakeQuery.data === null) {
            router.replace('/');
        }
    }, [hasStockTakeQuery.data])

    const [stocktake, setStocktake] = useState<StocktakeDto>();

    const stocktakeValid = useMemo(() => {
        let validityEndDate = stockService.calculateValidityEndDate(stocktake);
        let stillValid = time.now().valueOf() < validityEndDate.valueOf();
        let validityStatuses = [Enums.StocktakeStatus.Counting, Enums.StocktakeStatus.CountingComplete, Enums.StocktakeStatus.Pending, Enums.StocktakeStatus.Draft];
        return !validityStatuses.includes(stocktake?.Status ?? -1) || stillValid;
    }, [stocktake]);

    const { data, isLoading, refetch: refetchStocktake } = useQuery<StocktakeDto>({
        queryKey: ['stocktake', router.query.id], // TODO(tanstack-router): verify router.query → useParams/useSearch
        queryFn: async () => {
            // Don't make the request if there's no ID
            if (!router.query.id) return null; // TODO(tanstack-router): verify router.query → useParams/useSearch

            // Make the API call to fetch StockTakeItems for the given stockTake ID
            const response = await Fetch.get({
                url: `/Stocktake/` + router.query.id, // TODO(tanstack-router): verify router.query → useParams/useSearch
            });

            if (!response.ID) {
                Error(response.serverMessage || response.message || 'Failed to fetch stock take items');
            }

            return response;
        },
        // Only run the query if we have a router.query.id // TODO(tanstack-router): verify router.query → useParams/useSearch
        enabled: !!router.query.id // TODO(tanstack-router): verify router.query → useParams/useSearch
    });

    // Update stocktake state when query data changes
    useEffect(() => {
        if (data) {
            setStocktake(data);
        }
    }, [data])

    const { data: stocktakeFinancials, isFetching: isLoadingFinancials, refetch: refetchFinancials } = useQuery<StockTakeFinancialsDto>({
        queryKey: ['stocktakeFinancials', router.query.id], // TODO(tanstack-router): verify router.query → useParams/useSearch
        queryFn: async () => {
            // Don't make the request if there's no ID
            if (!router.query.id) return null; // TODO(tanstack-router): verify router.query → useParams/useSearch

            // Make the API call to fetch StockTakeItems for the given stockTake ID
            const response = await Fetch.get({
                url: `/Stocktake/totals/financial/` + router.query.id, // TODO(tanstack-router): verify router.query → useParams/useSearch
            });

            if (!response.ID) {
                Error(response.serverMessage || response.message || 'Failed to fetch stock take items');
            }

            return response;
        },
        // Only run the query if we have a router.query.id // TODO(tanstack-router): verify router.query → useParams/useSearch
        enabled: !!router.query.id // TODO(tanstack-router): verify router.query → useParams/useSearch
    });

    // Company currency symbol
    const { data: currencySymbol } = useQuery(
        ['companyCurrencySymbol'],
        () => fetchCompanyCurrencySymbol(),
        { refetchOnWindowFocus: false }
    );

    // Add this mutation to the StockTakeItemList component
    const queryClient = useQueryClient();
    const stocktakeMutation = useMutation({
        mutationFn: async (st: StocktakeDto) => {
            const response = await Fetch.put({
                url: `/Stocktake/${stocktake?.ID}`,
                params: {
                    Stocktake: st
                }
            });

            if (response?.serverMessage || response?.message || response.errors) {
                throw new Error(
                    response?.serverMessage ||
                    response?.message ||
                    (response.errors && JSON.stringify(response.errors)) ||
                    'Failed to update stocktake'
                );
            }
            return response;
        },
        onSuccess: async (stocktake: StocktakeDto) => {
            // Invalidate relevant queries to refresh data
            queryClient.invalidateQueries(['stockTakeItems', stocktake.ID]);
            queryClient.invalidateQueries(['stocktake', stocktake.ID]);
            queryClient.invalidateQueries(['stocktakeFinancials', stocktake.ID]);

            showNotification({
                message: 'Stock take updated',
                color: 'scBlue',
            });

            setStocktake(stocktake);

            if (stocktake.Status === enums.StocktakeStatus.Counting || stocktake.Status === enums.StocktakeStatus.CountingComplete || stocktake.Status === enums.StocktakeStatus.StocktakeComplete) {
                await Helper.waitABit()
                setActiveTab('items')
                // tab access takes too long to reflect - easier to set tab directly
            } else {
                handleTabChange('details')
            }

            // Refetch the current stocktake data if needed
            // refetch();
        },
        onError: (error: Error) => {
            console.error('Failed to update stocktake:', error);
            showNotification({
                message: 'Failed to update stocktake: ' + error.message,
                color: 'yellow.7',
            });
        }
    });

    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

    const handleCancel = () => {
        if (stocktake) {
            setConfirmOptions({
                ...Helper.initialiseConfirmOptions(),
                display: true,
                heading: "Cancel " + stocktake.Name,
                text: "Are you sure you want to cancel this stock take?",
                confirmButtonText: "Cancel Stock Take",
                cancelButtonText: "Back",
                showCancel: true,
                onConfirm: async () => {
                    const updatedStocktake = { ...stocktake, Status: enums.StocktakeStatus.Cancelled };
                    stocktakeMutation.mutate(updatedStocktake);
                }
            })
        }
    }

    const handleFinalize = () => {
        /*if(filteredStock.length === 0) {
            showNotification({
                id: 'addstocktakeitems',
                message: 'Please add Stocktake items',
                color: 'yellow.7'
            })
        } else */
if (stocktake?.Status === enums.StocktakeStatus.Draft) {
            setConfirmOptions({
                ...Helper.initialiseConfirmOptions(),
                display: true,
                heading: "Finalize Draft",
                text: "Please try to finalize your list with all items included and verified. While modifications will be possible afterward, completing your list beforehand ensures a smoother stocktake process.",
                confirmButtonText: "Finalize",
                discardButtonText: "",
                showCancel: true,
                onConfirm: async () => {
                    stocktakeMutation.mutate({
                        ...stocktake,
                        Status: enums.StocktakeStatus.Pending
                    })
                }
            })
        }
    }

    const handleStart = () => {
        if (stocktake) {
            const updatedStocktake: StocktakeDto = { ...stocktake, Status: enums.StocktakeStatus.Counting, StartedDate: new Date().toISOString().split('.')[0] };
            stocktakeMutation.mutate(updatedStocktake);
        }
    }

    const handleCompleteCounting = async () => {
        const stillCounting = filteredStock.some(x => x.CountedQuantity === null /*x.Status === enums.StocktakeItemStatus.Pending || x.Status === enums.StocktakeItemStatus.Recount*/)
        if (stillCounting) {
            showNotification({
                id: 'stillcounting',
                message: 'Some items are still being counted',
                color: 'yellow.7'
            })
        } else if (stocktake?.Status === enums.StocktakeStatus.Counting) {
            await Helper.waitABit()
            stocktakeMutation.mutate({
                ...stocktake,
                Status: enums.StocktakeStatus.CountingComplete,
            })
        }
    }

    const handleRecount = () => {
        if (stocktake?.Status === enums.StocktakeStatus.CountingComplete) {
            stocktakeMutation.mutate({
                ...stocktake,
                Status: enums.StocktakeStatus.Counting,
            })
        }
    }

    const handleCompleteStocktake = async () => {
        const stillCounting = filteredStock.some(x => x.CountedQuantity === null /*x.Status === enums.StocktakeItemStatus.Pending || x.Status === enums.StocktakeItemStatus.Recount*/)
        // discrepancies are now handled on the server
        if (stillCounting) {
            showNotification({
                id: 'stillcounting',
                message: 'Some items are still being counted',
                color: 'yellow.7'
            })
        } /*else if (someDiscrepancies) {
            showNotification({
                id: 'unresolvedDiscrepancies',
                message: 'Please resolve all discrepancies',
                color: 'yellow.7'
            })
        } */else if (stocktake?.Status === enums.StocktakeStatus.CountingComplete) {

            await refreshConflicts()

            setConfirmOptions({
                ...Helper.initialiseConfirmOptions(),
                display: true,
                heading: "Complete Stock Take",
                text: `
                ${conflictItemInventoryIds.length > 0 ? `<strong> Warning: </strong>${conflictItemInventoryIds.reduce((a, c) => a.includes(c) ? a : [...a, c], [] as string[]).length} items are being counted in another stocktake. This could result in duplicate stock level adjustments. <br> <br>` : ''}
                This action will perform stock level adjustments based on the values provided. Please ensure all counts and adjustments are accurate before proceeding. 
                `,
                confirmButtonText: "Finalize",
                discardButtonText: "",
                showCancel: true,
                onConfirm: async () => {
                    stocktakeMutation.mutate({
                        ...stocktake,
                        Status: enums.StocktakeStatus.StocktakeComplete,
                        CompletedDate: new Date().toISOString().split('.')[0]
                    })
                }
            })
        }
    }

    const [activeTab, setActiveTab] = useState<string | null>('items');

    const setInitialTab = (tabDataLocal = tabData) => {
        const tab = router.query.tab as string // TODO(tanstack-router): verify router.query → useParams/useSearch
        if (tab && tabDataLocal[tab] && tabDataLocal[tab].access !== false) {
            setActiveTab(tab)
        }
        else {
            setActiveTab(Object.entries(tabDataLocal).find(([, x]) => (x as any).access)?.[0] || 'details');
        }
    };

    const tabData = useMemo(() => {
        let tabs: any = {
            details: {
                label: 'Details',
                access: true
            },
            items: {
                label: 'Items',
                access: stocktake?.Status !== enums.StocktakeStatus.Draft && stocktake?.Status !== enums.StocktakeStatus.Pending && stocktake?.Status !== enums.StocktakeStatus.Cancelled,
            },
            /*discrepancies: {
                label: 'Discrepancies',
                access: !stocktake || stocktake?.Status !== enums.StocktakeStatus.Draft
            },*/
        };

        setInitialTab(tabs);

        return tabs;
    }, [stocktake])

    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

    const getAccessStatus = () => {
        let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (subscriptionInfo) {
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    const [stockAdmin] = useState(PS.hasPermission(Enums.PermissionName.StockTakeManager));
    const isAssignedEmployee = useMemo(() => {
        return stocktake && storageService.getCookie(Enums.Cookie.employeeID) === stocktake.AssignedEmployeeID
    }, [stocktake])

    useEffect(() => {
        getAccessStatus();
    }, []);

    useEffect(() => {
        if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
            Helper.nextRouter(Router.replace, "/");
        }

    }, [accessStatus/*, stockAdmin*/]);

    const handleTabChange = (newTabName: string | null) => {
        if (newTabName && tabData[newTabName].access !== false) {
            // If switching to details tab and items have changed, refresh details
            if (newTabName === 'details' && itemsChangedSinceLastDetailsView && refreshDetailsRef.current) {
                refreshDetailsRef.current();
                // Reset the flag after refreshing
                setItemsChangedSinceLastDetailsView(false);
            }

            setActiveTab(newTabName);
            router.replace(router.asPath.split('?')[0] + '?tab=' + newTabName);
        }
        else {
            const [bestTabName] = Object.entries(tabData).find(([, x]) => (x as any).access) || [];

            // If switching to details tab and items have changed, refresh details
            if (bestTabName === 'details' && itemsChangedSinceLastDetailsView && refreshDetailsRef.current) {
                refreshDetailsRef.current();
                // Reset the flag after refreshing
                setItemsChangedSinceLastDetailsView(false);
            }

            setActiveTab(bestTabName || 'details');
            router.replace(router.asPath.split('?')[0] + '?tab=' + bestTabName);
        }
    }

    const [stocktakeItems, setStocktakeItems] = useState<StocktakeItemDto[]>([]);
    const filteredStock = useMemo(() => stocktakeItems?.filter(x => x.IsActive) ?? [], [stocktakeItems]);

    const [conflictItemInventoryIds, setConflictItemInventoryIds] = useState<string[]>([]);
    const { data: conflictItems, refetch: refreshConflicts } = useQuery(
        ['stocktake-conflicts', router.query.id], // TODO(tanstack-router): verify router.query → useParams/useSearch
        async () => {
            if (!filteredStock.length) return [];

            const response = await Fetch.get({
                url: '/Stocktake/item/conflicts',
                params: {
                    stocktakeId: router.query.id, // TODO(tanstack-router): verify router.query → useParams/useSearch
                    // inventoryIds: transferredItems.map(item => item.ID)
                }
            });

            if (response?.Results) {
                return response.Results;
            } else {
                throw new Error(response.serverMessage || response.message || 'Failed to fetch conflict items');
            }
        },
        {
            enabled: !!router.query.id && filteredStock.length > 0, // TODO(tanstack-router): verify router.query → useParams/useSearch
        }
    );

    // Update conflict item inventory IDs when query data changes
    useEffect(() => {
        if (conflictItems) {
            setConflictItemInventoryIds(conflictItems.map(x => x.InventoryID))
        }
    }, [conflictItems])

    const [hasUnsentChanges, setHasUnsentChanges] = useState(false);

    const debouncedFincancialRefresh = useDebouncedCallback(() => refetchFinancials(), 2000)

    useDidUpdate(() => {
        console.log('hasUnsentChanges', hasUnsentChanges)
        if(!hasUnsentChanges) { // new changes was sent to the server
            debouncedFincancialRefresh()
        }
    }, [hasUnsentChanges]);

    // Track fetching state from the item list component
    const [itemListFetching, setItemListFetching] = useState(false);

    // Combined fetching state
    const isFetching = itemListFetching || isLoadingFinancials;

    // Reference to the StockTakeItemList's refresh function
    const refreshItemsRef = useRef<() => void>();

    // Reference to the StockTakeDetail's refresh function
    const refreshDetailsRef = useRef<() => void>();

    // Track if items have been changed since last details tab view
    const [itemsChangedSinceLastDetailsView, setItemsChangedSinceLastDetailsView] = useState(false);

    const preventNextItemsChangedSinceLastDetailsViewState = useRef(false);

    // Function to handle manual refresh
    const handleRefresh = useCallback(() => {
        // The next update needs to be prevented as items onChanged is triggered again with items refresh
        preventNextItemsChangedSinceLastDetailsViewState.current = true;
        // Call the refresh function from StockTakeItemList if available
        refreshItemsRef.current?.();

        // Call the refresh function from StockTakeDetail if available
        refreshDetailsRef.current?.();

        // Refresh the financials data
        refetchFinancials();

        refetchStocktake();

        // Reset the items changed flag after manual refresh
        setItemsChangedSinceLastDetailsView(false);
    }, [refetchFinancials]);

    const tabs = (
        <Flex w={'100%'} align={'end'}>
            <Tabs value={activeTab} onChange={handleTabChange}
                classNames={{
                    tab: tabStyles.scTab,
                    list: tabStyles.scTabList,
                    tabLabel: tabStyles.scTabLabel
                }}
            >
                <Tabs.List mb={0} pb={0}>
                    {
                        Object.entries(tabData).filter(([, x]) => true).map(
                            ([tabName, d]: any) => d &&
                                <Tooltip label={d.disabledLabel || ''}
                                    key={tabName + 'tab'}
                                    position={'bottom'}
                                    disabled={!d.disabled || !d.disabledLabel}
                                    color={'scBlue'}
                                    openDelay={1000}
                                    events={{ hover: true, focus: true, touch: true }}
                                >
                                    <Tabs.Tab value={tabName}
                                        disabled={!d.access || d.disabled}
                                    >
                                        <Flex align={'center'} gap={5}>
                                            {d.label} {/*{(tabName === '') && <NewText />}*/}
                                        </Flex>
                                    </Tabs.Tab>
                                </Tooltip>
                        )
                    }
                </Tabs.List>
            </Tabs>

            <Flex gap={{ base: 3, md: "md" }} ml="auto" mb={'xs'} wrap={'wrap'} >
                <Flex gap="xs">
                                <Link href={'/inventory/list?tab=stocktake'} style={{ marginLeft: 'auto' }}><Button variant="subtle" leftSection={<IconChevronLeft size={16} />}>Back to Stock Takes</Button></Link>
                                <Button
                                    variant="subtle"
                                    color="scBlue"
                                    onClick={handleRefresh}
                                    leftSection={
                                        <IconRefresh
                                            size={16}
                                            style={isFetching ? {transition: '2s ease-in-out'} : {}}
                                            className={isFetching ? styles.rotate : ''}
                                        />
                                    }
                                >
                                    Refresh
                                </Button>
                                </Flex>

                {
                    stockAdmin && <>

                        {/*<Tooltip label={'Copy Stock Take'} color={'scBlue'} openDelay={500} disabled={!buttonIconMode}>
                            <Link
                                href={`/stock-take/${router.query.id}/copy`} // TODO(tanstack-router): verify router.query → useParams/useSearch
                            >
                                {
                                    buttonIconMode ?
                                        <ActionIcon variant={'subtle'} size={'lg'} h={36} mr={2}>
                                            <IconCopy/>
                                        </ActionIcon>
                                        :
                                        <Button variant={'subtle'} leftSection={<IconCopy size={17}/>} size={'sm'}>
                                            Copy
                                        </Button>
                                }
                            </Link>
                        </Tooltip>*/}

                        {/*<Tooltip label={'Edit Stock Take'} color={'scBlue'} openDelay={500} disabled={!buttonIconMode}>
                                <Link
                                    href={`/stock-take/${router.query.id}/edit`} // TODO(tanstack-router): verify router.query → useParams/useSearch
                                >
                                    {
                                        buttonIconMode ?
                                            <ActionIcon variant={'subtle'} size={'lg'} h={36} mr={2}>
                                                <IconEdit/>
                                            </ActionIcon>
                                            :
                                            <Button variant={'subtle'} leftSection={<IconEdit size={17}/>} size={'sm'}>
                                                Edit
                                            </Button>
                                    }
                                </Link>
                            </Tooltip>*/}

                        {
                            (stocktake?.Status !== enums.StocktakeStatus.StocktakeComplete && stocktake?.Status !== enums.StocktakeStatus.CountingComplete) &&
                            (
                                stocktake?.Status !== enums.StocktakeStatus.Cancelled ?
                                    <Button
                                        disabled={stocktakeMutation.isLoading}
                                        leftSection={
                                            // stocktakeMutation.isLoading ? <Loader color={'yellow.7'} size={16} /> :
                                            <IconCircleX size={17} />
                                        }
                                        ml={'auto'}
                                        size={'sm'}
                                        variant={'outline'}
                                        color="yellow.7"
                                        onClick={handleCancel}
                                    >
                                        Cancel Stock Take
                                    </Button> :
                                    <Flex align="center" gap="sm" ml={'auto'}>
                                        <Text fw={500} c="yellow.7">
                                            Cancelled:
                                        </Text>
                                        <Button
                                            disabled={stocktakeMutation.isLoading}
                                            leftSection={
                                                // stocktakeMutation.isLoading ? <Loader color="yellow.7" size={16}/> :
                                                <IconRefresh size={17} />
                                            }
                                            size="sm"
                                            variant="subtle"
                                            color="gray.7"
                                            onClick={() => {
                                                if (stocktake) {
                                                    const updatedStocktake = {
                                                        ...stocktake,
                                                        Status: enums.StocktakeStatus.Pending,
                                                    };
                                                    stocktakeMutation.mutate(updatedStocktake);
                                                }
                                            }}
                                        >
                                            Reopen Stocktake
                                        </Button>
                                    </Flex>
                            )
                        }

                        {
                            stocktake?.Status === enums.StocktakeStatus.Draft/* && stocktakeItems.length !== 0*/ &&
                            <Button ml={'auto'} color={enums.StocktakeStatusText[enums.StocktakeStatus.Pending].color} leftSection={stocktakeMutation.isLoading ? <Loader color={'white'} size={16} /> : <IconChecklist size={17} />} size={'sm'} onClick={handleFinalize}>
                                Finalize Draft
                            </Button>
                        }

                        {
                            stocktake?.Status === enums.StocktakeStatus.Pending && (
                                <Button
                                    leftSection={stocktakeMutation.isLoading ? <Loader color={'white'} size={16} /> : <IconPlayerPlay size={17} />}
                                    size={'sm'}
                                    ml={'auto'}
                                    color={enums.StocktakeStatusText[enums.StocktakeStatus.Counting].color}
                                    onClick={handleStart}
                                >
                                    Start Counting
                                </Button>
                            )
                        }

                        {
                            stocktake?.Status === enums.StocktakeStatus.CountingComplete && (
                                <Button
                                    leftSection={stocktakeMutation.isLoading ? <Loader color={'scBlue'} size={16} /> : <IconArrowBackUp size={17} />}
                                    size={'sm'}
                                    ml={'auto'}
                                    variant={'outline'}
                                    color={enums.StocktakeStatusText[enums.StocktakeStatus.Counting].color}
                                    onClick={() => {
                                        handleRecount();
                                    }}
                                >
                                    Recount
                                </Button>
                            )
                        }

                        {
                            stocktake?.Status === enums.StocktakeStatus.CountingComplete && (
                                <Button
                                    leftSection={
                                    stocktakeMutation.isLoading ? <Loader color={'white'} size={16} /> :
                                        hasUnsentChanges ? <Loader type={'bars'} color={'scBlue.3'} size={12} /> :
                                        <IconCheckupList size={17} />
                                    }
                                    size={'sm'}
                                    ml={'auto'}
                                    color={enums.StocktakeStatusText[enums.StocktakeStatus.StocktakeComplete].color}
                                    onClick={() => {
                                        handleCompleteStocktake();
                                    }}
                                    disabled={hasUnsentChanges || !stocktakeValid}
                                >
                                    Complete Stock Take
                                </Button>
                            )
                        }

                    </>
                }

                {
                    (stockAdmin || isAssignedEmployee) && <>
                        {
                            stocktake?.Status === enums.StocktakeStatus.Counting && (
                                <Button
                                    leftSection={
                                        stocktakeMutation.isLoading ? <Loader color={'white'} size={16} /> :
                                            hasUnsentChanges ? <Loader type={'bars'} color={'scBlue.3'} size={12} /> :
                                                <IconCheck size={17} />
                                    }
                                    size={'sm'}
                                    ml={'auto'}
                                    color={enums.StocktakeStatusText[enums.StocktakeStatus.CountingComplete].color}
                                    onClick={() => {
                                        handleCompleteCounting();
                                    }}
                                    disabled={hasUnsentChanges || !stocktakeValid}
                                    title={!stocktakeValid ? "Stock take has expired. Increase the validity period to continue work" : ""}
                                >
                                    Complete Counting
                                </Button>
                            )
                        }
                    </>
                }

            </Flex>

        </Flex>
    )

    return <>

        <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

        <Box
            mt={'var(--mantine-spacing-sm)'}
            px={'var(--mantine-spacing-md)'}
            mb={0}
            pb={0}
            style={(theme) => ({ borderBottom: `${theme.colors.gray[2]} 2px solid` })}
        >
            <Flex my={'md'} align={'center'} gap={'sm'}>
                {stocktake && <>
                    <Breadcrumbs
                        currPage={{
                            text: stocktake?.Name || 'Stock Take',
                            link: `/stock-take/${router.query.id}?tab=${activeTab || ''}`, // TODO(tanstack-router): verify router.query → useParams/useSearch
                            type: 'view'
                        }}
                    />
                </>

                }
            </Flex>
            {tabs}
        </Box>

        <Box
            mih={`calc(100vh - 138px${((messageBarContext as any)?.isActive ? ` - ${constants.messageBarMargin}px` : "")})`}
            style={(theme) => ({
                backgroundColor: theme.colors.gray[1]
            })}
            p={'var(--mantine-spacing-xs)'}
        >

            {
                isLoading &&
                <Flex align={'center'} justify={'center'} direction={'column'} mih={'220px'}>
                    <Loader size={40} />
                </Flex>
            }

            {
                stocktake &&
                <Tabs value={activeTab} keepMounted={false}>
                    <Tabs.Panel keepMounted={false} value="items">
                        <>
                            <Flex align={'stretch'} justify={'center'} gap={'xs'} direction={{base: 'column', md: 'row-reverse'}}
                                  mb={'xs'}
                            >
                                <Card
                                    flex={3}
                                    style={{
                                        flexGrow: 1,
                                        alignSelf: 'stretch',
                                        backgroundColor: 'white'
                                    }}
                                    p={'var(--mantine-spacing-md)'}
                                    radius={'md'}
                                    mx={'auto'}
                                    pr={20}
                                    w={{base: '100%', md: 'auto'}}
                                >
                                    <StocktakeTimeline currentStatus={stocktake.Status} minimal />
                                </Card>

                                <Card
                                    flex={2}
                                    style={{
                                        flexGrow: 1,
                                        alignSelf: 'stretch',
                                        backgroundColor: 'white'
                                    }}
                                    p={'var(--mantine-spacing-md)'}
                                    radius={'md'}
                                    mx={'auto'}
                                    pr={20}
                                    w={{ base: '100%', md: 'auto' }}
                                >
                                    <Flex align={'center'} justify={'center'} gap={'sm'} mx={'auto'} h={'100%'}>
                                        {stocktake && <>
                                            {/*<Box miw={150} px={30} >
                                        <ScStatusData extraStyles={{transform: 'scale(1.5)', translate: '0% 0%'}} value={enums.StocktakeStatusText[stocktake.Status].label} color={enums.StocktakeStatusText[stocktake.Status].color} />
                                    </Box>*/}
                                            <Text size="md">
                                        <span style={{color: 'var(--mantine-color-gray-7)'}}>
                                            Warehouse:
                                        </span> &nbsp;
                                                {stocktake?.WarehouseName}
                                            </Text>
                                            <Text size="md" ml={5}>
                                        <span style={{ color: 'var(--mantine-color-gray-7)' }}>
                                            Assignee:
                                        </span> &nbsp;
                                                {stocktake?.AssignedEmployeeFullName}
                                            </Text>
                                        </>
                                        }
                                    </Flex>
                                    {stocktakeFinancials && (
                                        <Flex justify="center" align={'center'} gap={'md'} mt={'xs'}>
                                            {isLoadingFinancials && false ? (
                                                <>
                                                    <Skeleton height={19} width={180} radius="sm"/>
                                                    <Skeleton height={19} width={180} radius="sm"/>
                                                    {stocktakeFinancials?.AdjustedCost !== 0 && (
                                                        <Skeleton height={19} width={180} radius="sm"/>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <Text size="sm">
                                                    <span style={{color: 'var(--mantine-color-gray-7)'}}>
                                                        Expected Value:
                                                    </span>
                                                        &nbsp;{currencySymbol ? `${currencySymbol} ${stocktakeFinancials?.ExpectedCost?.toFixed(2)}` : stocktakeFinancials?.ExpectedCost?.toFixed(2)}
                                                    </Text>
                                                    <Text size="sm">
                                                    <span style={{color: 'var(--mantine-color-gray-7)'}}>
                                                        Counted Value:
                                                    </span>
                                                        &nbsp;{currencySymbol ? `${currencySymbol} ${stocktakeFinancials?.CountedCost?.toFixed(2)}` : stocktakeFinancials?.CountedCost?.toFixed(2)}
                                                    </Text>
                                                    {stocktake.Status === enums.StocktakeStatus.CountingComplete && (
                                                        <Text size="sm">
                                                        <span style={{color: 'var(--mantine-color-gray-7)'}}>
                                                            Adjustment Value:
                                                        </span>
                                                            &nbsp;{currencySymbol ? `${currencySymbol} ${stocktakeFinancials?.AdjustedCost?.toFixed(2)}` : stocktakeFinancials?.AdjustedCost?.toFixed(2)}
                                                        </Text>
                                                    )}
                                                </>
                                            )}
                                        </Flex>
                                    )}
                                </Card>
                            </Flex>

                            <Card
                                style={{
                                    backgroundColor: 'white'
                                }}
                                p={'var(--mantine-spacing-md)'}
                                radius={'md'}
                            >
                                <StockTakeItemList
                                    stocktake={stocktake}
                                    onChange={(items) => {
                                        setStocktakeItems(items);
                                        // Mark that items have been changed since last details view
                                        if (!preventNextItemsChangedSinceLastDetailsViewState.current) {
                                            setItemsChangedSinceLastDetailsView(true);
                                        } else {
                                            preventNextItemsChangedSinceLastDetailsViewState.current = false;
                                        }
                                    }}
                                    onUnsentChangesChange={setHasUnsentChanges}
                                    conflictItemInventoryIds={conflictItemInventoryIds}
                                    onFetchingChange={setItemListFetching}
                                    onRefresh={(refresh) => {
                                        refreshItemsRef.current = refresh;
                                    }}
                                />
                            </Card>
                        </>
                    </Tabs.Panel>
                    <Tabs.Panel keepMounted={false} value="details">
                        <>
                            <StockTakeDetail
                                stocktake={stocktake}
                                onView={() => handleTabChange('items')}
                                financials={stocktakeFinancials}
                                isLoadingFinancials={isLoadingFinancials}
                                currencySymbol={currencySymbol || ''}
                            />
                        </>
                    </Tabs.Panel>
                </Tabs>
            }

        </Box>
    </>
};

export default StockTake;

export const Route = createFileRoute('/_authenticated/stock-take/$id')({ component: StockTake })
